/**
 * Cloudinary URL utilities - optimización y watermarks
 *
 * Watermark opcional: solo si existe en tu cuenta Cloudinary.
 * Definí `VITE_CLOUDINARY_WATERMARK_ID` en .env / Railway (no hay fallback en código: un public_id
 * borrado en Cloudinary rompe todas las URLs con overlay con 400).
 *
 * Opciones:
 * 1. public_id: VITE_CLOUDINARY_WATERMARK_ID=watermark-anaafernandez
 * 2. URL completa: VITE_CLOUDINARY_WATERMARK_ID=https://res.cloudinary.com/TU_CLOUD/image/upload/...
 *
 * @see https://cloudinary.com/documentation/image_transformations#apply_an_image_overlay
 */
const WATERMARK_SOURCE = (
  (import.meta.env.VITE_CLOUDINARY_WATERMARK_ID as string) || ""
).trim();

export const isCloudinaryWatermarkConfigured = (): boolean =>
  WATERMARK_SOURCE.length > 0;

const CLOUD_NAME =
  (import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string)?.trim() || "";

import { BRAND_CLOUDINARY_FOLDER } from "./brandConfig";

export const CLOUDINARY_BASE_FOLDER =
  (import.meta.env.VITE_CLOUDINARY_BASE_FOLDER as string)?.trim() || BRAND_CLOUDINARY_FOLDER;

/** Cloud name (VITE_CLOUDINARY_CLOUD_NAME). Exported for UI display. */
export const CLOUDINARY_CLOUD_NAME = CLOUD_NAME;

/** Slug para carpeta Cloudinary (ej: "Campaign Look" → "campaign-look") */
export function slugifyFolder(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 50) || "seccion";
}

/**
 * Construye la ruta de carpeta Cloudinary para una sección opcional.
 * Centraliza la lógica: base_folder / slug(title).
 * @example buildCloudinaryFolder("Campaign Look") → "anaafernandez/portfolio/campaign-look"
 * @example buildCloudinaryFolder() → "anaafernandez/portfolio"
 */
export function buildCloudinaryFolder(title?: string): string {
  return title?.trim()
    ? `${CLOUDINARY_BASE_FOLDER}/${slugifyFolder(title)}`
    : CLOUDINARY_BASE_FOLDER;
}

/**
 * Shape de un asset de Cloudinary devuelto por /api/cloudinary/list-folder.
 * Fuente canónica para el frontend — el backend tiene una copia local.
 */
export interface CloudinaryAsset {
  public_id: string;
  secure_url: string;
  width: number;
  height: number;
  bytes: number;
  format: string;
  created_at: string;
}

/**
 * Resuelve logo/header: acepta URL completa o public_id de Cloudinary.
 * Si es public_id (sin http), construye la URL con VITE_CLOUDINARY_CLOUD_NAME.
 */
export function resolveLogoUrl(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  const v = value.trim();
  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  if (!CLOUD_NAME) return v;
  return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${v}`;
}

/**
 * Resuelve URL de imagen del portfolio. Si es public_id corto (sin /),
 * antepone la carpeta de la sección: anaafernandez/portfolio/{slug-título}/{id}
 */
export function resolvePortfolioImageUrl(
  value: string | undefined,
  sectionTitle?: string
): string | undefined {
  if (!value?.trim()) return undefined;
  const v = value.trim();
  if (v.startsWith("http://") || v.startsWith("https://")) return resolveLogoUrl(v);
  if (!CLOUD_NAME) return v;
  // public_id corto (ej: 2_y6kdit) → anteponer carpeta de sección
  if (!v.includes("/") && sectionTitle) {
    const folder = `${CLOUDINARY_BASE_FOLDER}/${slugifyFolder(sectionTitle)}`;
    return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${folder}/${v}`;
  }
  return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${v}`;
}

/** Indica si la URL es de Cloudflare Images (imagedelivery.net). No se usan en galería. */
export function isCloudflareImage(value: string | undefined): boolean {
  return !!value?.trim() && value.includes("imagedelivery.net");
}

/**
 * Indica si una URL o public_id es de Cloudinary.
 * URLs finales (workaround) deben funcionar sí o sí.
 */
export function isCloudinaryImage(value: string | undefined): boolean {
  if (!value?.trim()) return false;
  const v = value.trim();
  // URL final Cloudinary - cualquier formato que contenga res.cloudinary.com
  if (v.includes("res.cloudinary.com")) return true;
  if (v.startsWith("http://") || v.startsWith("https://")) return false;
  return !!CLOUD_NAME && v.length > 0 && !v.includes(" ") && !v.includes("\n");
}

/**
 * Indica si el logo (URL o public_id) coincide con una URL de galería.
 * Usado para excluir el logo de la galería.
 */
export function isSameCloudinaryImage(
  logoValue: string | undefined,
  galleryUrl: string
): boolean {
  if (!logoValue?.trim() || !galleryUrl.includes("res.cloudinary.com"))
    return false;
  const logo = logoValue.trim();
  if (logo.startsWith("http")) {
    return galleryUrl.trim() === logo;
  }
  const parsed = parseCloudinaryUrl(galleryUrl);
  if (!parsed) return false;
  const logoPublicId = logo.replace(/\.[^.]+$/, "");
  return (
    parsed.publicId === logoPublicId ||
    parsed.publicId.endsWith("/" + logoPublicId)
  );
}

/**
 * Parámetros del watermark (opcional, valores por defecto)
 */
export interface WatermarkOptions {
  /** Posición: south_east (esquina inferior derecha), south_west, center, etc. */
  gravity?: string;
  /** Offset X en píxeles */
  x?: number;
  /** Offset Y en píxeles */
  y?: number;
  /** Ancho del watermark: 0.1-0.3 = 10-30% del ancho de la imagen */
  widthScale?: number;
  /** Opacidad 0-100 */
  opacity?: number;
}

const DEFAULT_WATERMARK: Required<WatermarkOptions> = {
  gravity: "south_east",
  x: 8,
  y: 8,
  widthScale: 0.08,
  opacity: 25,
};

/** Sin redimensionar la base antes del overlay, algunos JPG devuelven 400 en Cloudinary. */
const WATERMARK_BASE_FALLBACK = "w_1600,c_limit";

/** Detecta si es URL (http/https) */
function isWatermarkUrl(source: string): boolean {
  return source.startsWith("http://") || source.startsWith("https://");
}

/** Extrae cloud y public_id de una URL de Cloudinary */
function parseCloudinaryUrl(
  url: string
): { cloudName: string; publicId: string } | null {
  const m = url.match(
    /^https:\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\/(.+)$/
  );
  if (!m) return null;
  const [, cloudName, path] = m;
  const publicId = path.replace(/^v\d+\//, "").replace(/\.[^.]+$/, "");
  return { cloudName, publicId };
}

/**
 * Formato public_id para overlay: Cloudinary requiere / reemplazado por :
 * @see https://cloudinary.com/documentation/layers#image_overlays
 */
function formatPublicIdForOverlay(publicId: string): string {
  return publicId.replace(/\//g, ":");
}

/**
 * Base64 encode para URLs - compatible con Unicode.
 * Cloudinary requiere base64 con padding.
 */
function base64EncodeUrl(url: string): string {
  try {
    return btoa(url);
  } catch {
    return btoa(String.fromCharCode(...new TextEncoder().encode(url)));
  }
}

/**
 * Construye la cadena de overlay para Cloudinary
 * - public_id: l_<id> (misma cuenta, más fiable)
 * - URL Cloudinary misma cuenta: l_<public_id extraído>
 * - URL externa: l_fetch:<base64>
 */
function buildOverlayString(
  opts: WatermarkOptions | undefined,
  imageCloudName?: string
): string {
  const { gravity, x, y, widthScale, opacity } = {
    ...DEFAULT_WATERMARK,
    ...opts,
  };
  let layer: string;

  if (isWatermarkUrl(WATERMARK_SOURCE)) {
    const parsed = parseCloudinaryUrl(WATERMARK_SOURCE);
    if (parsed && imageCloudName && parsed.cloudName === imageCloudName) {
      layer = `l_${formatPublicIdForOverlay(parsed.publicId)}`;
    } else {
      layer = `l_fetch:${base64EncodeUrl(WATERMARK_SOURCE)}`;
    }
  } else {
    layer = `l_${formatPublicIdForOverlay(WATERMARK_SOURCE)}`;
  }

  // Capa: escala relativa y opacidad; luego aplicar gravedad (doc: …/c_scale,fl_relative,w_0.5/o_50/fl_layer_apply…)
  return `${layer}/c_scale,fl_relative,w_${widthScale}/o_${opacity}/fl_layer_apply,g_${gravity},x_${x},y_${y}`;
}

/**
 * Aplica transformaciones a una URL de Cloudinary: optimización + watermark opcional
 *
 * El watermark se aplica si `VITE_CLOUDINARY_WATERMARK_ID` apunta a un asset existente.
 * Pasa watermark: false para desactivarlo en una imagen concreta.
 */
export function getCloudinaryUrl(
  url: string,
  options?: {
    width?: number;
    height?: number;
    crop?: "fill" | "fit" | "limit" | "scale" | "thumb";
    gravity?: string;
    watermark?: boolean | WatermarkOptions;
    quality?: "auto" | "auto:best" | "auto:eco" | number;
    format?: "auto" | "webp" | "avif" | "jpg" | "png";
  }
): string {
  if (!url.includes("res.cloudinary.com")) return url;

  const match = url.match(
    /^(https:\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\/)(.+)$/
  );
  if (!match) return url;

  const [, base, cloudName, path] = match;
  const parts: string[] = [];

  const applyWatermark =
    !!WATERMARK_SOURCE && options?.watermark !== false;

  // Redimensionar / limitar la base antes del overlay
  if (options?.width || options?.height) {
    const crop = options.crop ?? "fill";
    const dims = [
      options.width ? `w_${options.width}` : "",
      options.height ? `h_${options.height}` : "",
      `c_${crop}`,
      options.gravity ? `g_${options.gravity}` : "",
    ].filter(Boolean).join(",");
    parts.push(dims);
  } else if (applyWatermark) {
    parts.push(WATERMARK_BASE_FALLBACK);
  }

  // Watermark encima de la base ya transformada
  if (applyWatermark) {
    const overlay =
      options?.watermark === true || options?.watermark === undefined
        ? buildOverlayString(undefined, cloudName)
        : buildOverlayString(options.watermark as WatermarkOptions, cloudName);
    parts.push(overlay);
  }

  const quality = options?.quality ?? "auto";
  const format = options?.format ?? "auto";
  parts.push(`q_${quality},f_${format}`);

  const transform = parts.join("/");
  return `${base}${transform}/${path}`;
}

/**
 * Genera una URL de thumbnail cuadrado optimizado para grillas de admin.
 * @param url URL Cloudinary
 * @param size Tamaño en px (default 200)
 */
export function getCloudinaryThumbUrl(url: string, size = 200): string {
  return getCloudinaryUrl(url, {
    width: size,
    height: size,
    crop: "thumb",
    gravity: "face:auto",
    watermark: false,
    quality: "auto:eco",
  });
}

/**
 * Genera un srcset para imágenes responsivas. Devuelve vacío si no es Cloudinary.
 * @example <img srcSet={buildCloudinrarySrcSet(url)} sizes="(max-width:768px) 100vw, 50vw" />
 */
export function buildCloudinrarySrcSet(
  url: string,
  widths = [400, 800, 1200, 1600],
): string {
  if (!url.includes("res.cloudinary.com")) return "";
  return widths
    .map((w) => `${getCloudinaryUrl(url, { width: w, crop: "limit", watermark: false })} ${w}w`)
    .join(", ");
}

/**
 * Construye una URL de video de Cloudinary a partir del public_id.
 * Aplica q_auto, f_auto (si el browser lo soporta) y ancho opcional.
 */
export function getCloudinaryVideoUrl(
  publicIdOrUrl: string,
  options?: { width?: number; quality?: "auto" | number },
): string {
  if (!publicIdOrUrl?.trim()) return "";
  const quality = options?.quality ?? "auto";

  if (publicIdOrUrl.includes("res.cloudinary.com")) {
    // URL completa de video — insertar transformaciones
    const m = publicIdOrUrl.match(
      /^(https:\/\/res\.cloudinary\.com\/([^/]+)\/video\/upload\/)(.+)$/
    );
    if (!m) return publicIdOrUrl;
    const [, base, , path] = m;
    const transforms = [
      options?.width ? `w_${options.width},c_limit` : "",
      `q_${quality},f_auto`,
    ].filter(Boolean).join("/");
    return `${base}${transforms}/${path}`;
  }

  if (!CLOUD_NAME) return publicIdOrUrl;
  const transforms = [
    options?.width ? `w_${options.width},c_limit` : "",
    `q_${quality},f_auto`,
  ].filter(Boolean).join("/");
  const sep = transforms ? `${transforms}/` : "";
  return `https://res.cloudinary.com/${CLOUD_NAME}/video/upload/${sep}${publicIdOrUrl}`;
}

/**
 * Resuelve public_id a URL y aplica optimización + watermark.
 * Solo para imágenes Cloudinary. Para otras URLs retorna undefined (no mostrar).
 */
export function resolveAndOptimizeCloudinaryUrl(
  value: string | undefined,
  options?: { width?: number; height?: number; watermark?: boolean; crop?: "fill" | "fit" | "limit" }
): string | undefined {
  if (!value?.trim() || !isCloudinaryImage(value)) return undefined;
  const resolved = resolveLogoUrl(value);
  if (!resolved?.includes("res.cloudinary.com")) return undefined;
  return getCloudinaryUrl(resolved, { ...options, watermark: options?.watermark !== false });
}
